import React, { useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import dateLanguages from 'src/utilities/dateFnsLocales';
// @mui
import { styled } from '@mui/material/styles';
import { Avatar, Box, Link, Typography } from '@mui/material';
// components
import Image from './Image';
import { useSelector } from 'react-redux';
import AudioMessagePlayer from './AudioMessagePlayer';
import ChatMessageItemStatus from './ChatMessageItemStatus';
import { useTranslation } from 'react-i18next';

const ChatMessageItem = ({
  message,
  conversation,
  onOpenLightbox,
  onResendMessage,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { selectedLang } = useSelector((state) => state.language);
  const { id: userId } = useSelector((state) => state.user);
  const sender = conversation.participants.find(
    (participant) => participant.id === message.senderId
  );

  const senderDetails =
    message.senderId === userId
      ? { type: 'me' }
      : { avatar: sender?.profilePicture, name: sender?.name };
  const isMe = senderDetails.type === 'me';
  const firstName = senderDetails.name && senderDetails.name.split(' ')[0];

  const isImage = message.type === 'image';
  const messageProps = {
    ...(isImage && { onOpenLightbox: onOpenLightbox(message.body) }),
  };

  const renderMessageContent = (message, messageProps) => {
    if (message.type === 'image') {
      return (
        <Image
          alt="attachment"
          src={message.body}
          {...messageProps}
          sx={{
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        />
      );
    }

    if (message.type === 'audio') {
      return <AudioMessagePlayer src={message.body} />;
    }

    return (
      <MessageStyle isMe={isMe}>
        <Typography>{message.body}</Typography>
      </MessageStyle>
    );
  };

  const handleResend = async (message) => {
    setLoading(true);
    try {
      await onResendMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RootStyle>
      <Box
        sx={{
          display: 'flex',
          ...(isMe && {
            ml: 'auto',
          }),
          maxWidth: '75%',
        }}
      >
        {senderDetails.type !== 'me' && (
          <Avatar
            alt={senderDetails.name}
            src={senderDetails.avatar}
            sx={{ width: 32, height: 32, mr: 2 }}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: senderDetails.type === 'me' ? 'flex-end' : 'flex-start',
          }}
        >
          <ContentStyle
            sx={{
              ...(isMe && {
                color: 'grey.800',
                bgcolor: 'primary.lighter',
              }),
              ...(isImage && {
                p: 0,
              }),
            }}
          >
            {renderMessageContent(message, messageProps)}
          </ContentStyle>
          <InfoStyle
            variant="caption"
            sx={{
              ...(isMe && { justifyContent: 'flex-end' }),
              ...(message.error && {
                color: 'error.main',
              }),
            }}
          >
            <ChatMessageItemStatus message={message} loading={loading} />
            {!isMe && `${firstName},`}&nbsp;
            {message.error ? (
              <Link
                component="button"
                variant="body2"
                onClick={() => handleResend(message)}
                sx={{
                  color: 'inherit',
                  fontSize: 'inherit',
                }}
                disabled={loading}
              >
                {t('chat.message_error_retry')}
              </Link>
            ) : (
              formatDistanceToNowStrict(new Date(message.createdAt), {
                addSuffix: true,
                locale: dateLanguages[selectedLang],
              })
            )}
          </InfoStyle>
        </Box>
      </Box>
    </RootStyle>
  );
};

export default React.memo(ChatMessageItem);

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: theme.spacing(3),
}));

const ContentStyle = styled('div')(({ theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
}));

const InfoStyle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const MessageStyle = styled(Typography)(({ theme, isMe }) => ({
  variant: 'body2',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  color: isMe
    ? theme.palette.getContrastText(theme.palette.primary.light)
    : theme.palette.text.primary,
}));